import React, { useState, useEffect } from "react";
import { Clock, ChevronRight, UserX } from "lucide-react";
import { Issue, IssueStatus, UserProfile } from "../types";
import { formatSlaCountdown } from "../utils/slaCalculator";
import { getAppPillClasses } from "../utils/appColors";
import { CategoryBadge } from "./CategoryBadge";
import { hasUnreadResponse } from "../utils/notificationTracker";

export interface IssueKanbanViewProps {
  issues: Issue[];
  onSelectIssue: (issue: Issue) => void;
  onShowAllStatuses?: () => void;
  isStatusFiltered?: boolean;
  currentUser?: UserProfile;
}

interface ColumnConfig {
  id: string;
  title: string;
  statuses: IssueStatus[];
  headerColor: string;
}

const KANBAN_COLUMNS: ColumnConfig[] = [
  {
    id: "new",
    title: "New / Unassigned",
    statuses: ["new"],
    headerColor: "bg-blue-500",
  },
  {
    id: "in_review",
    title: "In Review",
    statuses: ["in_review"],
    headerColor: "bg-purple-500",
  },
  {
    id: "in_progress",
    title: "In Progress",
    statuses: ["in_progress"],
    headerColor: "bg-teal-500",
  },
  {
    id: "pending_user_feedback",
    title: "Pending Feedback",
    statuses: ["pending_user_feedback"],
    headerColor: "bg-amber-500",
  },
  {
    id: "resolved",
    title: "Resolved & Closed",
    statuses: ["resolved", "closed"],
    headerColor: "bg-emerald-500",
  },
];

export const IssueKanbanView: React.FC<IssueKanbanViewProps> = ({
  issues,
  onSelectIssue,
  onShowAllStatuses,
  isStatusFiltered,
  currentUser,
}) => {
  const [, setReadTick] = useState(0);

  useEffect(() => {
    const handleUpdate = () => {
      setReadTick((prev) => prev + 1);
    };
    window.addEventListener("mlc-ticket-read-updated", handleUpdate);
    return () => {
      window.removeEventListener("mlc-ticket-read-updated", handleUpdate);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 items-start">
      {KANBAN_COLUMNS.map((col) => {
        const colIssues = issues.filter((i) => col.statuses.includes(i.status));

        return (
          <div
            key={col.id}
            className="glass rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col max-h-[calc(100vh-220px)]"
          >
            {/* Column Header */}
            <div className="p-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`w-2.5 h-2.5 rounded-full ${col.headerColor}`}
                />
                <h3 className="font-bold text-slate-800 text-xs tracking-tight">
                  {col.title}
                </h3>
              </div>
              <span className="px-1.5 py-0.2 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold">
                {colIssues.length}
              </span>
            </div>

            {/* Column Body Cards */}
            <div className="p-2.5 space-y-2.5 overflow-y-auto flex-1">
              {colIssues.length === 0 ? (
                col.id === "resolved" &&
                onShowAllStatuses &&
                isStatusFiltered ? (
                  <div className="p-4 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50/60 my-1 space-y-1.5">
                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                      Resolved &amp; closed tickets hidden by default
                    </p>
                    <button
                      type="button"
                      onClick={onShowAllStatuses}
                      className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 underline cursor-pointer"
                    >
                      Show Resolved &amp; Closed
                    </button>
                  </div>
                ) : (
                  <div className="p-6 text-center text-slate-400 text-xs italic">
                    Empty
                  </div>
                )
              ) : (
                colIssues.map((issue) => {
                  const slaCountdown = issue.sla
                    ? formatSlaCountdown(issue.sla.targetAssignTime)
                    : null;

                  const isUnassigned =
                    !issue.assigneeUid && !issue.assigneeEmail;

                  return (
                    <div
                      key={issue.id}
                      onClick={() => onSelectIssue(issue)}
                      className={`p-3 bg-white rounded-xl border border-slate-200 shadow-xs hover:shadow-md hover:border-teal-300 transition-all cursor-pointer space-y-2 group ${
                        isUnassigned ? "border-l-4 border-l-amber-400" : ""
                      }`}
                    >
                      {/* Top Badges */}
                      <div className="flex items-center justify-between gap-1.5 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[10px] font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-200">
                            {issue.id}
                          </span>
                          <CategoryBadge category={issue.category} size="sm" />
                          {hasUnreadResponse(issue, currentUser) && (
                            <span
                              data-testid={`kanban-unread-badge-${issue.id}`}
                              title="New response received"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-rose-100 text-rose-700 border border-rose-200 shadow-2xs animate-in fade-in duration-150"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                              <span>New Response</span>
                            </span>
                          )}
                        </div>
                        <span
                          className={`px-1.5 py-0.2 rounded font-semibold uppercase text-[9px] ${
                            issue.priority === "critical"
                              ? "bg-rose-100 text-rose-800"
                              : issue.priority === "high"
                                ? "bg-amber-100 text-amber-800"
                                : issue.priority === "medium"
                                  ? "bg-sky-100 text-sky-800"
                                  : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {issue.priority}
                        </span>
                      </div>

                      {/* Title */}
                      <h4 className="font-semibold text-slate-900 text-xs line-clamp-2 group-hover:text-teal-700 transition-colors">
                        {issue.title}
                      </h4>

                      {/* App Tag */}
                      <div>
                        <span
                          className={`inline-block font-bold text-xs px-2.5 py-0.5 rounded-full border shadow-2xs ${getAppPillClasses(issue.appName)}`}
                        >
                          {issue.appName}
                        </span>
                      </div>

                      {/* Bottom Strip: SLA & Assignee */}
                      <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between text-[10px]">
                        {isUnassigned ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              data-testid={`kanban-unassigned-badge-${issue.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-900 border border-amber-300 shadow-2xs"
                            >
                              <UserX className="w-2.5 h-2.5 text-amber-600" />
                              <span>Unassigned</span>
                            </span>
                            {slaCountdown && (
                              <span
                                className={`inline-flex items-center gap-1 font-semibold ${
                                  slaCountdown.isBreached
                                    ? "text-rose-600"
                                    : slaCountdown.isWarning
                                      ? "text-amber-600"
                                      : "text-slate-500"
                                }`}
                              >
                                <Clock className="w-3 h-3" />
                                {slaCountdown.text}
                              </span>
                            )}
                          </div>
                        ) : slaCountdown && !issue.assigneeUid ? (
                          <span
                            className={`inline-flex items-center gap-1 font-semibold ${
                              slaCountdown.isBreached
                                ? "text-rose-600"
                                : slaCountdown.isWarning
                                  ? "text-amber-600"
                                  : "text-slate-500"
                            }`}
                          >
                            <Clock className="w-3 h-3" />
                            {slaCountdown.text}
                          </span>
                        ) : (
                          <span className="text-slate-600 font-medium">
                            {issue.assigneeEmail
                              ? issue.assigneeEmail.split("@")[0]
                              : "Unassigned"}
                          </span>
                        )}
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
